import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link2, ArrowRight, Info } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JoinByCode = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await axios.post(`${API}/invitations/accept/code`, { code: code.toUpperCase() });
      
      toast.success(response.data.message || 'Contrat lié avec succès !');
      setTimeout(() => navigate('/contrats'), 1000);
    } catch (error) {
      console.error('Error joining contract:', error);
      const detail = error.response?.data?.detail || 'Code invalide ou expiré';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="container-custom py-6 space-y-6" data-testid="join-by-code-page">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'var(--font-secondary)' }}>
          Rejoindre un contrat
        </h1>
        <p className="text-[var(--text-secondary)] mt-1">
          Entrez le code d'invitation fourni par votre employeur
        </p>
      </div>
      
      <Card className="max-w-md">
        <CardHeader>
          <div className="mx-auto mb-2 w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--primary-100)' }}>
            <Link2 className="h-7 w-7" style={{ color: 'var(--primary-400)' }} />
          </div>
          <CardTitle className="text-center text-xl" style={{ fontFamily: 'var(--font-secondary)' }}>
            Code d'invitation
          </CardTitle>
          <CardDescription className="text-center">
            Entrez le code à 6 caractères communiqué par le parent employeur
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                type="text"
                placeholder="ABC123"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                maxLength={6}
                minLength={6}
                required
                className="text-center text-2xl font-mono font-bold tracking-widest"
                data-testid="join-code-input"
              />
              <p className="text-xs text-[var(--text-tertiary)] mt-1 text-center">
                6 caractères (lettres et chiffres)
              </p>
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading || code.length !== 6}
              style={{ backgroundColor: 'var(--primary-400)' }}
              data-testid="join-submit-button"
            >
              {loading ? 'Liaison en cours...' : (
                <>
                  Rejoindre le contrat <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
          
          <Alert className="mt-4 border-[var(--primary-200)] bg-[var(--primary-50)]">
            <Info className="h-4 w-4" style={{ color: 'var(--primary-400)' }} />
            <AlertDescription className="text-xs" style={{ color: 'var(--primary-700)' }}>
              Ce code vous a été communiqué par le parent employeur. Si vous n'avez pas reçu de code, demandez-lui de générer une invitation depuis son espace Contrats.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};

export default JoinByCode;
